import { useState, useEffect } from "react";

const usePWA = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [swRegistration, setSwRegistration] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    Notification.permission === "granted"
  );
  const [installPrompt, setInstallPrompt] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Register Service Worker
    registerSW();

    // Online/Offline listeners
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Install prompt listener
    const handleInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);

    // Detect if already installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };
    window.addEventListener("appinstalled", handleAppInstalled);

    // Check if running as PWA
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // Register Service Worker
  const registerSW = async () => {
    if (!("serviceWorker" in navigator)) return;

    try {
      const registration = await navigator.serviceWorker.register(
        "/service-worker.js",
        { scope: "/" }
      );

      setSwRegistration(registration);
      console.log("[PWA] Service Worker registered:", registration.scope);

      // Check for updates
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        newWorker?.addEventListener("statechange", () => {
          if (
            newWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            setUpdateAvailable(true);
          }
        });
      });
    } catch (error) {
      console.error("[PWA] Service Worker registration failed:", error);
    }
  };

  // Install PWA
  const installPWA = async () => {
    if (!installPrompt) return false;
    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === "accepted") {
        setIsInstalled(true);
        setIsInstallable(false);
        setInstallPrompt(null);
        return true;
      }
    } catch (error) {
      console.error("[PWA] Install failed:", error);
    }
    return false;
  };

  // Request notification permission
  const enableNotifications = async () => {
    if (!("Notification" in window)) return false;

    try {
      const permission = await Notification.requestPermission();
      const granted = permission === "granted";
      setNotificationsEnabled(granted);
      return granted;
    } catch (error) {
      console.error("[PWA] Notification permission failed:", error);
      return false;
    }
  };

  // Send local notification
  const sendNotification = (title, body, url = "/alerts") => {
    if (
      !("Notification" in window) ||
      Notification.permission !== "granted"
    )
      return;

    const notification = new Notification(title, {
      body,
      icon: "/logo192.png",
      badge: "/logo192.png",
      tag: "safirimesh",
    });

    notification.onclick = () => {
      window.focus();
      window.location.href = url;
      notification.close();
    };
  };

  // Apply update
  const applyUpdate = () => {
    swRegistration?.waiting?.postMessage({ type: "SKIP_WAITING" });
    window.location.reload();
  };

  return {
    isInstallable,
    isInstalled,
    isOnline,
    notificationsEnabled,
    updateAvailable,
    installPWA,
    enableNotifications,
    sendNotification,
    applyUpdate,
  };
};

export default usePWA;